// appointmentSlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IAppointment, IAppointmentInitialState } from "./appointmentModal";
import {
  createAppointment,
  deleteAppointment,
  getAllAppointments,
  getAppointmentByID,
  getAppointmentsByDoctorID,
  updateAppointment,
} from "./services/appointment.service";
import moment from "moment";

const today = moment().format("YYYY-MM-DD");

const initialState: IAppointmentInitialState = {
  loading: false,
  formLoading: false,
  error: "",
  AppointmentID: null,
  AppointmentList: [],
  AppointmentDetail: {
    ID: null,
    DoctorID: null,
    Fees: null,
    Date: today,
    AvailableTimeSlot: "",
    PatientName: "",
    MobileNo: "",
    EmailID: "",
    ReasonForVisit: "",
    IsActive: 1,
  },
  FilterAppointment: {
    PatientName: "",
    MobileNo: "",
    DoctorID: null,
    EmailID: "",
    Fees: null,
    FromDate: today,
    ToDate: today,
    Skip: 0,
    Limit: 10,
  },
};

const appointmentSlice = createSlice({
  name: "appointment",
  initialState,
  reducers: {
    clearAppointmentDetails: (state) => {
      state.AppointmentDetail = initialState.AppointmentDetail;
    },
    setAppointmentID: (state, action) => {
      state.AppointmentID = action.payload;
    },
    clearAppointmentID: (state) => {
      state.AppointmentID = initialState.AppointmentID;
    },
    setFilterAppointment: (state, action) => {
      state.FilterAppointment = action.payload;
    },
    clearFilterAppointment: (state) => {
      state.FilterAppointment = initialState.FilterAppointment;
    },
  },
  extraReducers: (builder) => {
    // GET ALL
    builder.addCase(getAllAppointments.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      getAllAppointments.fulfilled,
      (state, action: PayloadAction<IAppointment[]>) => {
        state.loading = false;
        state.AppointmentList = action.payload || [];
      },
    );
    builder.addCase(getAllAppointments.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.AppointmentList = [];
    });

    // CREATE
    builder.addCase(createAppointment.pending, (state) => {
      state.formLoading = true;
      state.error = "";
    });
    builder.addCase(createAppointment.fulfilled, (state) => {
      state.formLoading = false;
    });
    builder.addCase(createAppointment.rejected, (state, action) => {
      state.formLoading = false;
      state.error = action.error.message || "Something went wrong";
    });

    // UPDATE
    builder.addCase(updateAppointment.pending, (state) => {
      state.formLoading = true;
      state.error = "";
    });
    builder.addCase(updateAppointment.fulfilled, (state) => {
      state.formLoading = false;
    });
    builder.addCase(updateAppointment.rejected, (state, action) => {
      state.formLoading = false;
      state.error = action.error.message || "Something went wrong";
    });

    // DELETE
    builder.addCase(deleteAppointment.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(deleteAppointment.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(deleteAppointment.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });

    // GET BY ID
    builder.addCase(getAppointmentByID.pending, (state) => {
      state.formLoading = true;
      state.error = "";
    });
    builder.addCase(
      getAppointmentByID.fulfilled,
      (state, action: PayloadAction<IAppointment>) => {
        state.formLoading = false;
        state.AppointmentDetail = action.payload;
      },
    );
    builder.addCase(getAppointmentByID.rejected, (state, action) => {
      state.formLoading = false;
      state.AppointmentDetail = initialState.AppointmentDetail;
      state.error = action.error.message || "Something went wrong";
    });

    // GET BY DOCTOR ID
    builder.addCase(getAppointmentsByDoctorID.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      getAppointmentsByDoctorID.fulfilled,
      (state, action: PayloadAction<IAppointment[]>) => {
        state.loading = false;
        state.AppointmentList = action.payload || [];
      },
    );
    builder.addCase(getAppointmentsByDoctorID.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.AppointmentList = [];
    });
  },
});

export const {
  clearAppointmentDetails,
  setAppointmentID,
  clearAppointmentID,
  setFilterAppointment,
  clearFilterAppointment,
} = appointmentSlice.actions;

export default appointmentSlice.reducer;
